"use client";

import { motion } from "motion/react";
import { useEffect, useRef, useState } from "react";

interface LogoProps {
  size?: "sm" | "md" | "lg" | "xl";
  white?: boolean;
  iconOnly?: boolean;
  className?: string;
}

const dims = {
  sm: { h: 30, icon: 28, fs: 15, gap: 5 },
  md: { h: 38, icon: 34, fs: 19, gap: 7 },
  lg: { h: 48, icon: 44, fs: 25, gap: 9 },
  xl: { h: 60, icon: 54, fs: 32, gap: 11 },
};

/* ────────────────────────────────────────────
   Animated waveform icon — canvas-based for
   buttery-smooth organic wave animation
   ──────────────────────────────────────────── */
function WaveIcon({ s, white, uid }: { s: number; white: boolean; uid: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const frameRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = s * dpr;
    canvas.height = s * dpr;
    ctx.scale(dpr, dpr);

    let running = true;

    const draw = (t: number) => {
      if (!running) return;
      ctx.clearRect(0, 0, s, s);

      const pad = s * 0.15;
      const innerW = s - pad * 2;
      const innerH = s - pad * 2;
      const cx = s / 2;
      const cy = s / 2;
      const barCount = 9;
      const barW = innerW / (barCount * 2 - 1);
      const r = barW / 2;
      const time = t * 0.001;

      // Draw bars — always vibrant cyan/teal regardless of mode
      for (let i = 0; i < barCount; i++) {
        const x = pad + i * (barW * 2);

        // Organic height: layered sine waves
        const phase = (i / barCount) * Math.PI * 2;
        const wave1 = Math.sin(time * 2.4 + phase) * 0.35;
        const wave2 = Math.sin(time * 3.8 + phase * 1.7) * 0.2;
        const wave3 = Math.sin(time * 1.5 + phase * 0.5) * 0.15;
        const norm = 0.3 + (wave1 + wave2 + wave3 + 0.7) * 0.5;
        const barH = Math.max(barW, norm * innerH);

        const y = cy - barH / 2;

        // Gradient per bar — vibrant cyan/teal, shifts over time
        const grad = ctx.createLinearGradient(x, y, x, y + barH);
        const hue1 = 185 + Math.sin(time + i * 0.3) * 10;
        const hue2 = 170 + Math.sin(time * 0.7 + i * 0.5) * 15;
        grad.addColorStop(0, `hsl(${hue1}, 85%, 52%)`);
        grad.addColorStop(0.5, `hsl(${(hue1 + hue2) / 2}, 80%, 48%)`);
        grad.addColorStop(1, `hsl(${hue2}, 75%, 45%)`);

        ctx.beginPath();
        ctx.roundRect(x, y, barW, barH, r);
        ctx.fillStyle = grad;
        ctx.fill();

        // Glow on taller bars
        if (norm > 0.6) {
          ctx.save();
          // Stronger glow when in dark/white-text mode for better contrast on dark bg
          ctx.globalAlpha = white ? (norm - 0.6) * 0.9 : (norm - 0.6) * 0.5;
          ctx.shadowColor = "rgba(6,182,212,0.85)";
          ctx.shadowBlur = white ? s * 0.18 : s * 0.08;
          ctx.beginPath();
          ctx.roundRect(x, y, barW, barH, r);
          ctx.fill();
          ctx.restore();
        }
      }

      // Subtle horizontal reflection line
      ctx.save();
      const reflectGrad = ctx.createLinearGradient(pad, 0, s - pad, 0);
      const reflAlpha = 0.06 + Math.sin(time * 2) * 0.03;
      reflectGrad.addColorStop(0, `rgba(${white ? "255,255,255" : "6,182,212"},0)`);
      reflectGrad.addColorStop(0.5, `rgba(${white ? "255,255,255" : "6,182,212"},${reflAlpha})`);
      reflectGrad.addColorStop(1, `rgba(${white ? "255,255,255" : "6,182,212"},0)`);
      ctx.fillStyle = reflectGrad;
      ctx.fillRect(pad, cy - 0.5, innerW, 1);
      ctx.restore();

      frameRef.current = requestAnimationFrame(draw);
    };

    frameRef.current = requestAnimationFrame(draw);
    return () => { running = false; cancelAnimationFrame(frameRef.current); };
  }, [s, white]);

  return (
    <div className="relative" style={{ width: s, height: s }}>
      {/* Outer glow pulse */}
      <motion.div
        className="absolute rounded-2xl"
        style={{
          inset: -2,
          background: white
            ? "rgba(255,255,255,0.15)"
            : "linear-gradient(135deg, rgba(6,182,212,0.25), rgba(20,184,166,0.2))",
          filter: `blur(${s * 0.15}px)`,
        }}
        animate={{ opacity: [0.5, 0.9, 0.5], scale: [0.95, 1.05, 0.95] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Glass container — transparent in dark/white-text mode (bars float clean) */}
      <div
        className="relative rounded-2xl overflow-hidden"
        style={{
          width: s,
          height: s,
          background: white
            ? "transparent"
            : "linear-gradient(145deg, rgba(6,182,212,0.08), rgba(20,184,166,0.04))",
          border: white ? "none" : "1px solid rgba(6,182,212,0.2)",
          backdropFilter: white ? undefined : "blur(8px)",
        }}
      >
        {/* Animated canvas waveform */}
        <canvas
          ref={canvasRef}
          style={{ width: s, height: s }}
          className="relative z-10"
        />

        {/* Top-left light reflection */}
        <div
          className="absolute top-0 left-0 w-1/2 h-1/3 rounded-br-full pointer-events-none"
          style={{
            background: `radial-gradient(ellipse at 20% 20%, ${white ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.08)"}, transparent 70%)`,
          }}
        />

        {/* Floating particles */}
        {[0, 1, 2].map(i => (
          <motion.div
            key={i}
            className="absolute rounded-full pointer-events-none"
            style={{
              width: s * 0.06,
              height: s * 0.06,
              background: white ? "rgba(255,255,255,0.5)" : "rgba(6,182,212,0.5)",
              left: `${25 + i * 25}%`,
              top: `${20 + i * 20}%`,
            }}
            animate={{
              y: [0, -(s * 0.12), 0],
              x: [0, (i % 2 === 0 ? 1 : -1) * s * 0.06, 0],
              opacity: [0, 0.7, 0],
              scale: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 3 + i * 0.7,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 1.2,
            }}
          />
        ))}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────
   Main Logo component
   ──────────────────────────────────────────── */
export function Logo({ size = "md", white = false, iconOnly = false, className = "" }: LogoProps) {
  const d = dims[size];
  const uid = `sr-${size}`;
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  return (
    <motion.div
      className={`flex items-center select-none ${className}`}
      style={{ height: d.h, gap: d.gap }}
      whileHover={{ scale: 1.03 }}
      transition={{ type: "spring", stiffness: 400, damping: 20 }}
    >
      {/* Icon — bars always vibrant cyan/teal; container transparent in dark mode */}
      {mounted ? (
        <WaveIcon s={d.icon} white={white} uid={uid} />
      ) : (
        <div style={{ width: d.icon, height: d.icon }} className="rounded-2xl bg-cyan-500/10" />
      )}

      {/* Text */}
      {!iconOnly && (
        <div className="relative overflow-hidden">
          <div
            className="tracking-tight leading-none whitespace-nowrap"
            style={{ fontSize: d.fs, fontFamily: "var(--font-neue-machina)", fontWeight: 800 }}
          >
            {white ? (
              <>
                <span className="text-white">Speak</span>
                <span className="text-white">Roar</span>
              </>
            ) : (
              <>
                <span className="text-neutral-900">Speak</span>
                <span className="text-neutral-900">Roar</span>
              </>
            )}
          </div>

          {/* Shimmer — mounted-gated to avoid hydration mismatch */}
          {mounted && (
            <motion.div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: `linear-gradient(105deg, transparent 30%, ${white ? "rgba(255,255,255,0.25)" : "rgba(6,182,212,0.12)"} 50%, transparent 70%)`,
                backgroundSize: "250% 100%",
                backgroundRepeat: "no-repeat",
              }}
              animate={{ backgroundPosition: ["-150% 0", "250% 0"] }}
              transition={{ duration: 4, repeat: Infinity, ease: "linear", repeatDelay: 3 }}
            />
          )}
        </div>
      )}
    </motion.div>
  );
}
